/**
 * Dialog para anular una venta (POST /sales/{id}/void).
 *
 * Backend requiere `reason` (mín 1 char). El void revierte el inventario vía
 * movimientos de ajuste; el correlativo del comprobante se mantiene "quemado"
 * (no se reusa) — eso lo gestiona el backend, acá solo pedimos el motivo.
 */

import { Loader2, Trash2, X } from "lucide-react";
import { useState, type FormEvent } from "react";

import { ApiError } from "@/lib/api.ts";
import { useVoidSale } from "@/queries/sales.ts";

interface Props {
  saleId: string;
  saleLabel?: string;
  onClose: () => void;
  onVoided?: () => void;
}

export function VoidSaleDialog({ saleId, saleLabel, onClose, onVoided }: Props) {
  const voidSale = useVoidSale();
  const [reason, setReason] = useState("");

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    voidSale.mutate(
      { sale_id: saleId, reason: reason.trim() },
      {
        onSuccess: () => {
          onVoided?.();
          onClose();
        },
      },
    );
  };

  const errorMsg =
    voidSale.error instanceof ApiError ? voidSale.error.message : voidSale.error?.message;

  const canSubmit = reason.trim().length >= 1 && !voidSale.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-100 p-2 text-red-700">
              <Trash2 className="h-5 w-5" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Anular venta</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={voidSale.isPending}
            aria-label="Cerrar"
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5">
          <p className="text-sm text-slate-600">
            Vas a anular {saleLabel ? <strong>{saleLabel}</strong> : "esta venta"}. El sistema va a
            revertir el inventario automáticamente. El número de comprobante queda quemado — no se
            reutiliza.
          </p>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Motivo de la anulación
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              required
              autoFocus
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/15"
              placeholder="Ej: error de digitación, cliente cambió de opinión, producto incorrecto…"
            />
          </div>

          {errorMsg && (
            <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {errorMsg}
            </p>
          )}

          <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={voidSale.isPending}
              className="rounded-lg px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:bg-slate-300"
            >
              {voidSale.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Anular
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
